import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  OpportunityHeader,
  OpportunityActions,
  OpportunityInfo,
  RolesPanel,
} from '../components/detail';
import {
  DceUploadButton,
  DceUploadsList,
  DceSearchPanel,
} from '../components/dce-upload';
import { DceAnalysisTab } from '../components/dce-analysis';
import { GoChecklistTab } from '../go-checklist/components/go-checklist-tab';
import {
  useOpportunity,
  useMakeDecision,
  useUpdateOpportunityStatus,
  useDeleteOpportunity,
  useRefreshOpportunityFromSource,
} from '../hooks/useOpportunities';
import {
  useOpportunityRoles,
  useCreateRole,
  useDeleteRole,
} from '../hooks/useRoles';
import { useTenantUsers } from '../hooks/useUsers';
import type { OpportunityStatus } from '../types/internal-opportunity.types';
import type { CreateRoleDto } from '../types/role.types';

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const opportunityId = id ? parseInt(id, 10) : undefined;

  const { data: opportunity, isLoading, error } = useOpportunity(opportunityId);
  const { data: roles = [], isLoading: rolesLoading } = useOpportunityRoles(opportunityId);
  const { data: users = [] } = useTenantUsers();

  const makeDecision = useMakeDecision();
  const updateStatus = useUpdateOpportunityStatus();
  const deleteOpportunity = useDeleteOpportunity();
  const refreshFromSource = useRefreshOpportunityFromSource();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole(opportunityId || 0);

  const handleDecision = (decision: 'go' | 'no_go', reason?: string) => {
    if (!opportunityId) return;

    makeDecision.mutate(
      { id: opportunityId, decision, reason },
      {
        onSuccess: () => {
          toast.success(
            `Décision ${decision === 'go' ? 'GO' : 'NO-GO'} enregistrée`
          );
        },
        onError: () => {
          toast.error("Erreur lors de l'enregistrement de la décision");
        },
      }
    );
  };

  const handleStatusChange = (status: OpportunityStatus) => {
    if (!opportunityId) return;

    updateStatus.mutate(
      { id: opportunityId, status },
      {
        onSuccess: () => {
          toast.success('Statut mis à jour');
        },
        onError: () => {
          toast.error('Erreur lors de la mise à jour du statut');
        },
      }
    );
  };

  const handleDelete = () => {
    if (!opportunityId) return;

    deleteOpportunity.mutate(opportunityId, {
      onSuccess: () => {
        toast.success('Opportunité supprimée');
        navigate('/tm/opportunities');
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      },
    });
  };

  const handleAddRole = (dto: CreateRoleDto) => {
    createRole.mutate(dto, {
      onSuccess: () => {
        toast.success('Membre ajouté');
      },
      onError: () => {
        toast.error("Erreur lors de l'ajout du membre");
      },
    });
  };

  const handleRemoveRole = (roleId: number) => {
    deleteRole.mutate(roleId, {
      onSuccess: () => {
        toast.success('Membre retiré');
      },
      onError: () => {
        toast.error('Erreur lors du retrait du membre');
      },
    });
  };

  const handleRefresh = () => {
    if (!opportunityId) return;

    const sourceLabel =
      opportunity?.importSource === 'ted'
        ? 'TED'
        : opportunity?.importSource === 'boamp'
          ? 'BOAMP'
          : 'la source';

    refreshFromSource.mutate(opportunityId, {
      onSuccess: () => {
        toast.success(`Données actualisées depuis ${sourceLabel}`);
      },
      onError: () => {
        toast.error("Erreur lors de l'actualisation des données");
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-copper" />
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-destructive mb-4">Opportunité non trouvée</p>
        <button
          onClick={() => navigate('/tm/opportunities')}
          className="text-copper hover:underline"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  const isActionLoading =
    makeDecision.isPending ||
    updateStatus.isPending ||
    deleteOpportunity.isPending ||
    refreshFromSource.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <OpportunityHeader opportunity={opportunity} />

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <DceUploadButton opportunityId={opportunityId!} />
        <OpportunityActions
          opportunity={opportunity}
          onDecision={handleDecision}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          isLoading={isActionLoading}
          isRefreshing={refreshFromSource.isPending}
        />
      </div>

      {/* Contenu avec onglets */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="team">
            Équipe ({roles.length})
          </TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="analysis">Analyse</TabsTrigger>
          <TabsTrigger value="go-checklist">GO / NO-GO</TabsTrigger>
          <TabsTrigger value="tasks" disabled>
            Tâches
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <OpportunityInfo opportunity={opportunity} />
        </TabsContent>

        <TabsContent value="team">
          <RolesPanel
            opportunityId={opportunityId!}
            roles={roles}
            users={users}
            isLoading={rolesLoading || createRole.isPending || deleteRole.isPending}
            onAddRole={handleAddRole}
            onRemoveRole={handleRemoveRole}
          />
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <DceUploadsList opportunityId={opportunityId!} />
          <DceSearchPanel opportunityId={opportunityId!} />
        </TabsContent>

        <TabsContent value="analysis">
          <DceAnalysisTab opportunityId={opportunityId!} />
        </TabsContent>

        <TabsContent value="go-checklist">
          <GoChecklistTab opportunityId={opportunityId!} />
        </TabsContent>

        <TabsContent value="tasks">
          <div className="text-center py-12 text-muted-foreground">
            Fonctionnalité à venir
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
